import { createApi } from '@reduxjs/toolkit/query/react';
import baseQueryWithAuth from './baseQueryWithAuth';

export const auditApi = createApi({
  reducerPath: 'auditApi',
  baseQuery: baseQueryWithAuth,
  tagTypes: ['Audit', 'CustomerAudit', 'UserAudit', 'Log'],
  endpoints: (builder) => ({
    // Get all audits with optional date filters
    getAllAudits: builder.query({
      query: ({ dateStart, dateEnd }) => ({
        url: '/audits',
        params: {
          ...(dateStart && { dateStart }),
          ...(dateEnd && { dateEnd }),
        },
      }),
      providesTags: ['Audit'],
    }),

    // Get audit by ID
    getAuditById: builder.query({
      query: (auditID) => `/audits/${auditID}`,
      providesTags: (result, error, auditID) => [{ type: 'Audit', id: auditID }],
    }),

    // Get audits by customer ID
    getAuditsByCustomerId: builder.query({
      query: ({ customerID, dateStart, dateEnd }) => ({
        url: '/audits/customer',
        params: {
          ...(dateStart && { dateStart }),
          ...(dateEnd && { dateEnd }),
        },
      }),
      providesTags: ['CustomerAudit'],
    }),

    // Get audits by user ID
    getAuditsByUserId: builder.query({
      query: (userID) => `/audits/user/${userID}`,
      providesTags: (result, error, userID) => [{ type: 'UserAudit', id: userID }],
    }),

    // Get all logs
    getAllLogs: builder.query({
      query: ({ dateStart, dateEnd }) => ({
        url: '/logs',
        params: {
          validUsers: true,
          excludeGet: true,
          ...(dateStart && { dateStart }),
          ...(dateEnd && { dateEnd }),
        },
      }),
      providesTags: ['Log'],
    }),

    // Create audit
    createAudit: builder.mutation({
      query: () => ({
        url: '/audit',
        method: 'POST',
      }),
      invalidatesTags: ['Audit', 'CustomerAudit'],
    }),

    // Update audit by ID
    updateAudit: builder.mutation({
      query: ({ auditID, ...patch }) => ({
        url: `/audit/${auditID}`,
        method: 'PUT',
        body: patch,
      }),
      invalidatesTags: (result, error, { auditID }) => [{ type: 'Audit', id: auditID }, 'CustomerAudit'],
    }),

    // Delete audit by ID
    deleteAudit: builder.mutation({
      query: (auditID) => ({
        url: `/audit/${auditID}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Audit', 'CustomerAudit'],
    }),

    // Get latest sync errors
    getLatestSyncErrors: builder.query({
      query: () => '/audits/sync/errors',
      // This doesn't need caching since it's for errors
      keepUnusedDataFor: 0,
    }),
  }),
});

// Export auto-generated hooks
export const {
  useGetAllAuditsQuery,
  useGetAuditByIdQuery,
  useGetAuditsByCustomerIdQuery,
  useGetAuditsByUserIdQuery,
  useGetAllLogsQuery,
  useCreateAuditMutation,
  useUpdateAuditMutation,
  useDeleteAuditMutation,
  useGetLatestSyncErrorsQuery,
} = auditApi;
